/**
 * Single source of truth for the campaign-management URL <-> Redux-view mapping.
 *
 * The campaign-management area (tabs + audience board) is driven by Redux state
 * (`activeTab` / `activeBulkView`). On the website we additionally reflect that state
 * in the URL so the tabs and the audience board are deep-linkable, mirroring how the
 * Tasks board uses `/tasks/?boardID=...`.
 *
 * IMPORTANT: these are PURE functions only (no React / browser / chrome deps) so they
 * run under the repo's node test environment. The React glue that calls them lives in
 * `use-restore-campaign-view-from-url.ts`, and the URL writes live at the click sites.
 * URL writes/reads are gated to the website (NOT the extension / WhatsApp view), because
 * driving react-router inside the extension context crashes it.
 */
import type { BulkDashboardTab, BulkView } from "@common-reducers/CampaignReducer";

/** Canonical paths for the campaign-management area on the website. */
export const CAMPAIGN_PATHS = {
  launchCampaign: "/launch-campaign",
  manageCampaigns: "/manage-campaigns",
  manageAudiences: "/manage-audiences",
  manageTemplates: "/manage-templates",
  audienceBoard: "/audience",
  // Template editor — `?templateId=…` distinguishes edit from create.
  newTemplate: "/new-template",
} as const;

/** The three management tabs that get their own path segment. */
const TAB_PATHS: ReadonlyArray<readonly [string, BulkDashboardTab]> = [
  [CAMPAIGN_PATHS.manageCampaigns, "manageCampaigns"],
  [CAMPAIGN_PATHS.manageAudiences, "manageAudiences"],
  [CAMPAIGN_PATHS.manageTemplates, "manageTemplates"],
];

/** Path segment for a given management tab (used when reflecting a tab click in the URL). */
export function tabToPath(tab: BulkDashboardTab): string {
  const match = TAB_PATHS.find(([, t]) => t === tab);
  return match ? match[0] : CAMPAIGN_PATHS.launchCampaign;
}

/** Deep-link URL for an audience board, matching the `/tasks/?boardID=` convention. */
export function audienceBoardPath(boardId: string): string {
  return `${CAMPAIGN_PATHS.audienceBoard}/?boardID=${boardId}`;
}

/** Deep-link URL for the template editor. Omit `templateId` for "new template",
 *  pass it for "edit template". Matches the audience-board `?boardID=` convention. */
export function newTemplatePath(templateId?: string): string {
  return templateId
    ? `${CAMPAIGN_PATHS.newTemplate}/?templateId=${templateId}`
    : CAMPAIGN_PATHS.newTemplate;
}

/**
 * True for any path that belongs to the campaign-management area. Used to keep existing
 * pathname-based checks (state-reset on unmount, template-dialog context detection) working
 * now that the area spans more than just `/launch-campaign`.
 */
export function isCampaignManagementPath(pathname: string): boolean {
  return (
    pathname.startsWith(CAMPAIGN_PATHS.launchCampaign) ||
    pathname.startsWith(CAMPAIGN_PATHS.manageCampaigns) ||
    pathname.startsWith(CAMPAIGN_PATHS.manageAudiences) ||
    pathname.startsWith(CAMPAIGN_PATHS.manageTemplates) ||
    pathname.startsWith(CAMPAIGN_PATHS.audienceBoard) ||
    pathname.startsWith(CAMPAIGN_PATHS.newTemplate)
  );
}

/** The Redux view a campaign-management path should restore to on load / navigation. */
export interface CampaignViewFromUrl {
  activeTab: BulkDashboardTab;
  activeBulkView: BulkView;
  /** When true the path is an audience board and `boardID` from the query selects the board. */
  isAudienceBoard: boolean;
  /** When true the path is the template editor and `templateId` from the query (if any)
   *  picks the template to edit (absent → "new template"). */
  isTemplateEditor?: boolean;
}

/**
 * Maps a pathname to the campaign view it should restore. Returns null for paths that are
 * not part of the campaign-management area (so the caller leaves Redux untouched).
 *
 * Note: only management-level views are restorable. Transient wizard sub-views
 * (new-campaign, new-audience, new-template) intentionally have no URL and are not restored.
 */
export function pathToCampaignView(pathname: string): CampaignViewFromUrl | null {
  if (pathname.startsWith(CAMPAIGN_PATHS.audienceBoard)) {
    return {
      activeTab: "manageAudiences",
      activeBulkView: "campaignBoardFromManagementView",
      isAudienceBoard: true,
    };
  }

  if (pathname.startsWith(CAMPAIGN_PATHS.newTemplate)) {
    return {
      activeTab: "manageTemplates",
      activeBulkView: "campaignNewTemplateViewFromManagement",
      isAudienceBoard: false,
      isTemplateEditor: true,
    };
  }

  const tabMatch = TAB_PATHS.find(([path]) => pathname.startsWith(path));
  if (tabMatch) {
    return {
      activeTab: tabMatch[1],
      activeBulkView: "campaignManagementView",
      isAudienceBoard: false,
    };
  }

  if (pathname.startsWith(CAMPAIGN_PATHS.launchCampaign)) {
    return {
      activeTab: "manageCampaigns",
      activeBulkView: "campaignManagementView",
      isAudienceBoard: false,
    };
  }

  return null;
}
